import { cn } from '@/lib/utils';
import { TriangleAlertIcon } from 'lucide-react';
import { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

interface ProcessTypeHintProps {
  id: string;
  /** False until the user has opened the process-type select or changed its value. */
  acknowledged: boolean;
  className?: string;
}

/**
 * Speech-bubble callout pointing down at the process-type select, shown when an
 * upload fits more than one process type. Loud (amber, with arrow) until the
 * user has touched the select, then a muted one-liner — never removed, and the
 * box keeps the same padding/border width in both states so nothing below it
 * moves when it softens.
 */
const ProcessTypeHint: FunctionComponent<ProcessTypeHintProps> = ({ id, acknowledged, className }) => {
  const { t } = useTranslation('project');
  return (
    <div
      id={id}
      role='status'
      data-acknowledged={acknowledged ? 'true' : 'false'}
      className={cn(
        'relative flex items-center gap-2 rounded-md border px-3 py-2 text-sm transition-colors',
        acknowledged
          ? 'border-transparent text-muted-foreground'
          : 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/70 dark:bg-amber-950 dark:text-amber-100',
        className,
      )}>
      <TriangleAlertIcon
        aria-hidden
        className={cn('size-4 shrink-0', acknowledged ? 'text-amber-500/80' : 'text-amber-600 dark:text-amber-400')}
      />
      <span>{acknowledged ? t('batch.processTypeHintShort') : t('batch.processTypeHint')}</span>
      {/* Bubble tail, aimed at the process-type select (first cell of the grid below). */}
      <span
        aria-hidden
        className={cn(
          'absolute -bottom-[5px] left-6 size-2.5 rotate-45 border-b border-r',
          acknowledged
            ? 'border-transparent bg-transparent'
            : 'border-amber-300 bg-amber-50 dark:border-amber-700/70 dark:bg-amber-950',
        )}
      />
    </div>
  );
};

export default ProcessTypeHint;
